import { useState } from 'react';

import { Button, Icon } from '@ds/core';
import { Tooltip } from '@ds/feedback';
import { Menu } from '@ds/navigation';
import { EtiquetaChip } from '@/comuns/EtiquetaChip';
import { useAviso } from '@/comuns/toast';
import type { Etiqueta } from '@/data/cadastros';
import type { EtiquetaAplicada } from '@/data/tipos';

export interface EtiquetasProps {
    /** O que dizer no `aria-label` do grupo: "Etiquetas da conversa", "Etiquetas de Clara Ribeiro". */
    rotulo: string;
    aplicadas: EtiquetaAplicada[];
    /** O cadastro da clínica. As inativas não são oferecidas, mas as já aplicadas continuam à vista. */
    disponiveis: Etiqueta[];
    aoAplicar: (etiquetaId: string) => Promise<void>;
    aoTirar: (etiquetaId: string) => Promise<void>;
    onMudou: () => void;
    /** Sem isto, só mostra — para tabela e cartão, onde não se edita. */
    editavel?: boolean;
    tamanho?: 'sm' | 'md';
}

/**
 * Etiquetas aplicadas, com aplicar e tirar à mão.
 *
 * Serve à conversa e à pessoa (0168): as duas saem da MESMA lista da clínica, então
 * duplicar este componente por alvo faria duas telas divergirem no primeiro ajuste. Quem
 * chama diz o que fazer ao aplicar e ao tirar; aqui só mora o comportamento comum.
 *
 * A que veio de gatilho diz isso — é o que deixa a equipe desconfiar de uma palavra-chave
 * larga demais ("valor" etiqueta muita coisa).
 */
export function Etiquetas({ rotulo, aplicadas, disponiveis, aoAplicar, aoTirar, onMudou, editavel = true, tamanho = 'sm' }: EtiquetasProps) {
    const aviso = useAviso();
    const [ocupado, setOcupado] = useState(false);

    const agir = async (acao: () => Promise<void>) => {
        setOcupado(true);
        try {
            await acao();
            onMudou();
        } catch (e) {
            aviso({ title: 'Não foi possível mudar a etiqueta', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setOcupado(false);
        }
    };

    const faltam = disponiveis.filter((e) => e.ativa && !aplicadas.some((a) => a.id === e.id));

    return (
        <div role="group" aria-label={rotulo} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {aplicadas.map((e) => (
                <span key={e.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <EtiquetaChip nome={e.nome} cor={e.cor} />
                    {e.origem === 'gatilho' && (
                        <Tooltip label="Aplicada por palavra-chave">
                            <span style={{ display: 'inline-flex', color: 'var(--neutral-500)' }}><Icon name="zap" size={12} /></span>
                        </Tooltip>
                    )}
                    {editavel && (
                        <button
                            type="button" aria-label={`Tirar a etiqueta ${e.nome}`} disabled={ocupado}
                            onClick={() => void agir(() => aoTirar(e.id))}
                            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--neutral-500)', padding: 2, display: 'flex' }}
                        >
                            <Icon name="x" size={12} />
                        </button>
                    )}
                </span>
            ))}
            {editavel && faltam.length > 0 && (
                <Menu
                    align="left" width={220}
                    onSelect={(id) => void agir(() => aoAplicar(id))}
                    trigger={<Button size={tamanho} variant="ghost" iconLeft={<Icon name="tag" size={13} />} disabled={ocupado}>Etiquetar</Button>}
                    items={faltam.map((e) => ({ id: e.id, label: e.nome, icon: 'tag' }))}
                />
            )}
            {/* Sem nenhuma e sem poder editar, a linha some em vez de deixar um vazio sem explicação. */}
            {!editavel && aplicadas.length === 0 && null}
        </div>
    );
}
